'use client';

import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

import { useMutation } from '@apollo/client/react';

import { uploadFileToStorage } from '@/shared-utilities';
import { AddMessageV3Input, CREATE_SIGNED_UPLOAD_URL } from '@/survey-graphql';

import {
  createWavHeader,
  shouldResetRecordingBufferOnModeChange,
} from './shared-survey-project-response-audio-record.helpers';
import { useProjectResponseModeContext } from './shared-survey-project-response-mode.hooks';
import type { ProjectResponseModeEvent } from './shared-survey-project-response-mode.types';

export default function useProjectResponseAudioRecord(
  orgId = '',
  isAnonymous: boolean | null | undefined = false
) {
  const recording = useRef<
    {
      data: Int16Array;
      date: number;
    }[]
  >([]);
  const lockedRef = useRef(false);

  const { audioDataObservable, eventsObservable, responseMode, getSampleRate } =
    useProjectResponseModeContext();
  const [createSignedUploadUrl] = useMutation(CREATE_SIGNED_UPLOAD_URL);

  const reset = useCallback(() => {
    recording.current = [];
  }, []);

  const onResponseModeChanged = useEffectEvent(
    (event: ProjectResponseModeEvent) => {
      if (event.type !== 'response_mode_changed') return;
      if (
        shouldResetRecordingBufferOnModeChange(event.next, lockedRef.current)
      ) {
        reset();
      }
    }
  );

  useEffect(() => {
    const subscription = audioDataObservable.subscribe((data) => {
      if (lockedRef.current) return;
      recording.current.push(data);
    });
    return () => subscription.unsubscribe();
  }, [audioDataObservable]);

  useEffect(() => {
    const subscription = eventsObservable.subscribe(onResponseModeChanged);
    return () => subscription.unsubscribe();
  }, [eventsObservable]);

  const lock = () => {
    lockedRef.current = true;
  };
  const unlock = () => {
    lockedRef.current = false;
  };

  const createAudioFile = useCallback(
    async (startDate = -Infinity): Promise<File | null> => {
      if (recording.current.length === 0) {
        return null;
      }

      const relevantRecording = recording.current.filter(
        (chunk) => chunk.date >= startDate
      );
      if (relevantRecording.length === 0) {
        return null;
      }

      try {
        const totalLength = relevantRecording.reduce(
          (total, chunk) => total + chunk.data.length,
          0
        );

        const combinedAudio = new Int16Array(totalLength);

        let offset = 0;
        for (const chunk of relevantRecording) {
          combinedAudio.set(chunk.data, offset);
          offset += chunk.data.length;
        }

        const audioBuffer = combinedAudio.buffer;

        const sampleRate = getSampleRate();

        const wavHeader = createWavHeader(
          combinedAudio.length,
          sampleRate,
          1,
          16
        );
        const wavBlob = new Blob([wavHeader, audioBuffer], {
          type: 'audio/wav',
        });

        const fileName = `recording-${Date.now()}.wav`;
        const audioFile = new File([wavBlob], fileName, { type: 'audio/wav' });

        return audioFile;
      } catch {
        return null;
      }
    },
    [getSampleRate]
  );

  const [createMessageAssetLoading, setCreateMessageAssetLoading] =
    useState(false);

  const createMessageAsset = useCallback(
    async (
      startDate = -Infinity,
      waitForUpload = true
    ): Promise<
      NonNullable<AddMessageV3Input['assets']>[number] | undefined
    > => {
      if (
        responseMode === 'text' ||
        recording.current.length === 0 ||
        isAnonymous
      )
        return undefined;
      const audioFile = await createAudioFile(startDate);
      if (!audioFile) return undefined;
      setCreateMessageAssetLoading(true);
      try {
        const { data } = await createSignedUploadUrl({
          variables: {
            input: {
              fileName: audioFile.name,
              orgId: orgId,
              mimeType: audioFile.type,
              isPublic: false,
            },
          },
        });
        if (
          !data?.createSignedUploadUrl.path ||
          !data?.createSignedUploadUrl.url
        ) {
          setCreateMessageAssetLoading(false);
          return undefined;
        }
        const uploadPromise = uploadFileToStorage(
          data.createSignedUploadUrl.url,
          audioFile
        );
        if (waitForUpload) {
          await uploadPromise;
        } else {
          void uploadPromise.catch(() => undefined);
        }
        const asset: NonNullable<AddMessageV3Input['assets']>[number] = {
          name: audioFile.name,
          gcsKey: data.createSignedUploadUrl.path,
          mimetype: audioFile.type,
          description: '',
        };
        return asset;
      } finally {
        setCreateMessageAssetLoading(false);
      }
    },
    /* oxlint-disable react/memo-dependencies */
    [responseMode, isAnonymous, createAudioFile, createSignedUploadUrl, orgId]
    /* oxlint-enable react/memo-dependencies */
  );

  return {
    reset,
    createMessageAsset,
    createMessageAssetLoading,
    lock,
    unlock,
  };
}
